import * as React from 'react'
import { useRoute } from '@/lib/RouteContext'
import { canonicalPath } from '@/lib/legacyPaths'
import { cn } from '@/lib/utils'
import { isBrowserClick, stretchedLinkClass } from '@/lib/linkClick'

export interface AppLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** The in-app path, exactly what `navigate()` would receive. */
  to: string
  /** Route params handed to `navigate()` on an in-app click. */
  params?: Record<string, string>
  /** Make the whole positioned ancestor clickable (see `stretchedLinkClass` in `@/lib/linkClick`). */
  stretched?: boolean
}

/**
 * A real `<a href>` for in-app navigation. A plain click keeps the SPA
 * navigation (`navigate()`, no reload); Cmd/Ctrl/Shift/Alt-click, the middle
 * button and the context menu's "open in new tab" get the real URL.
 */
export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
  ({ to, params, stretched = false, onClick, className, children, ...props }, ref) => {
    const { navigate } = useRoute()
    const href = canonicalPath(to)

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event)
      if (isBrowserClick(event)) return
      event.preventDefault()
      navigate(to, params)
    }

    return (
      <a
        ref={ref}
        href={href}
        onClick={handleClick}
        className={cn(
          stretched
            ? stretchedLinkClass
            : 'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          className
        )}
        {...props}
      >
        {children}
      </a>
    )
  }
)
AppLink.displayName = 'AppLink'
